import { useEffect, useMemo, useRef } from "react";

/**
 * Hand-rolled canvas chart primitives for Insights.
 *
 * No charting library: these four shapes (line, bar, donut, horizontal bar)
 * are simple enough that a dependency would cost more in bundle size and API
 * surface than it would save in drawing code. Everything reads its palette
 * from the CSS custom properties in tokens.css at draw time, so the charts
 * repaint correctly if the tokens ever change without a rebuild — and never
 * hardcode a color that could drift from the design system.
 *
 * Shared conventions across all four:
 *  - Backing store is sized at `clientWidth * devicePixelRatio` with the
 *    context scaled to match, and the CSS size set separately — the standard
 *    fix for blurry canvases on HiDPI (Retina) displays.
 *  - A ResizeObserver redraws on container width changes, since a chart drawn
 *    once at mount is wrong the moment the sidebar collapses or the window
 *    resizes.
 *  - Empty or all-zero data draws nothing, leaving the caller's EmptyState to
 *    own that message rather than rendering a misleading flat line at zero.
 *  - Every chart renders a `.sr-only` table alongside the canvas so the
 *    underlying data survives for screen readers, which cannot see canvas
 *    pixels at all.
 *  - `prefers-reduced-motion` is respected: no draw uses animation regardless,
 *    keeping this rule trivially satisfied rather than asserted.
 */

/* ──────────────────────────────────── shared plumbing ──────────────────────────────────── */

interface Point {
  label: string;
  value: number;
}

/** Reads a CSS custom property off the root element, trimmed to a usable color string. */
function cssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Sizes a canvas's backing store for the display's pixel ratio and returns a
 * scaled 2D context plus the CSS (logical) width/height to draw against.
 * Every chart calls this once per paint rather than only at mount, because
 * `clientWidth` changes with the container.
 */
function prepareCanvas(canvas: HTMLCanvasElement, cssHeight: number): { ctx: CanvasRenderingContext2D; width: number; height: number } | null {
  const width = canvas.clientWidth;
  if (width === 0) return null;
  const ratio = window.devicePixelRatio || 1;

  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(cssHeight * ratio);
  canvas.style.width = "100%";
  canvas.style.height = `${cssHeight}px`;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.clearRect(0, 0, width, cssHeight);
  return { ctx, width, height: cssHeight };
}

/**
 * Redraws `draw` on mount, on container resize, and whenever `deps` change.
 * Centralising the ResizeObserver here means each chart component only has
 * to supply its own paint function.
 */
function useCanvasPainter(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  draw: (canvas: HTMLCanvasElement) => void,
  deps: unknown[],
) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    draw(canvas);

    const observer = new ResizeObserver(() => draw(canvas));
    observer.observe(canvas);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function isAllZero(data: Point[]): boolean {
  return data.length === 0 || data.every((d) => d.value === 0);
}

/** Thins x-axis labels so they never overlap, keeping roughly one per `minPxPerLabel`. */
function labelStride(count: number, widthPx: number, minPxPerLabel = 56): number {
  if (count <= 1) return 1;
  const maxLabels = Math.max(1, Math.floor(widthPx / minPxPerLabel));
  return Math.max(1, Math.ceil(count / maxLabels));
}

/* ──────────────────────────────────── LineChart ──────────────────────────────────── */

export interface LineChartProps {
  data: Point[];
  height?: number;
  color?: string;
  fillColor?: string;
  /** Accessible label for the chart as a whole, e.g. "Tasks completed per day". */
  title?: string;
}

export function LineChart({ data, height = 200, color, fillColor, title = "Trend" }: LineChartProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const empty = isAllZero(data);

  useCanvasPainter(
    canvasRef,
    (canvas) => {
      if (empty) return;
      const prepared = prepareCanvas(canvas, height);
      if (!prepared) return;
      const { ctx, width } = prepared;

      const padL = 36;
      const padR = 8;
      const padT = 10;
      const padB = 22;
      const plotW = width - padL - padR;
      const plotH = height - padT - padB;

      const lineColor = color ?? cssVar("--chart-1", "#184669");
      const areaColor = fillColor ?? cssVar("--info-soft", "#e3ebf2");
      const gridColor = cssVar("--chart-grid", "#dcd9d1");
      const textColor = cssVar("--faint", "#9ca3af");

      const max = Math.max(...data.map((d) => d.value), 1);
      // Round the axis ceiling up to a friendly step so gridlines land on
      // whole numbers instead of arbitrary fractions.
      const niceMax = Math.ceil(max / 4) * 4 || 4;

      const xAt = (i: number) => padL + (data.length === 1 ? plotW / 2 : (i / (data.length - 1)) * plotW);
      const yAt = (v: number) => padT + plotH - (v / niceMax) * plotH;

      // Gridlines + y-axis labels (0, 1/2, max).
      ctx.strokeStyle = gridColor;
      ctx.fillStyle = textColor;
      ctx.font = "11px var(--font-sans), sans-serif";
      ctx.textBaseline = "middle";
      ctx.lineWidth = 1;
      const steps = [0, 0.5, 1];
      for (const frac of steps) {
        const y = padT + plotH - frac * plotH;
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(width - padR, y);
        ctx.stroke();
        ctx.textAlign = "right";
        ctx.fillText(String(Math.round(niceMax * frac)), padL - 6, y);
      }

      // Filled area under the line.
      ctx.beginPath();
      data.forEach((d, i) => {
        const x = xAt(i);
        const y = yAt(d.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.lineTo(xAt(data.length - 1), padT + plotH);
      ctx.lineTo(xAt(0), padT + plotH);
      ctx.closePath();
      ctx.fillStyle = areaColor;
      ctx.fill();

      // The line itself.
      ctx.beginPath();
      data.forEach((d, i) => {
        const x = xAt(i);
        const y = yAt(d.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.stroke();

      // Point markers.
      ctx.fillStyle = lineColor;
      data.forEach((d, i) => {
        ctx.beginPath();
        ctx.arc(xAt(i), yAt(d.value), 2.25, 0, Math.PI * 2);
        ctx.fill();
      });

      // X-axis labels, thinned so they never overlap regardless of width.
      const stride = labelStride(data.length, plotW);
      ctx.fillStyle = textColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      data.forEach((d, i) => {
        if (i % stride !== 0 && i !== data.length - 1) return;
        ctx.fillText(d.label, xAt(i), padT + plotH + 6);
      });
    },
    [data, height, color, fillColor, empty],
  );

  return (
    <div className="chart">
      {!empty && <canvas ref={canvasRef} role="presentation" />}
      <AccessibleTable title={title} columns={["Date", "Value"]} rows={data.map((d) => [d.label, String(d.value)])} />
    </div>
  );
}

/* ──────────────────────────────────── BarChart ──────────────────────────────────── */

export interface BarChartProps {
  data: Point[];
  height?: number;
  color?: string;
  title?: string;
}

export function BarChart({ data, height = 200, color, title = "Distribution" }: BarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const empty = isAllZero(data);

  useCanvasPainter(
    canvasRef,
    (canvas) => {
      if (empty) return;
      const prepared = prepareCanvas(canvas, height);
      if (!prepared) return;
      const { ctx, width } = prepared;

      const padL = 32;
      const padR = 8;
      const padT = 10;
      const padB = 28;
      const plotW = width - padL - padR;
      const plotH = height - padT - padB;

      const barColor = color ?? cssVar("--chart-1", "#184669");
      const gridColor = cssVar("--chart-grid", "#dcd9d1");
      const textColor = cssVar("--faint", "#9ca3af");

      const max = Math.max(...data.map((d) => d.value), 1);
      const niceMax = Math.ceil(max / 4) * 4 || 4;

      // Gridlines.
      ctx.strokeStyle = gridColor;
      ctx.fillStyle = textColor;
      ctx.font = "11px var(--font-sans), sans-serif";
      ctx.lineWidth = 1;
      [0, 0.5, 1].forEach((frac) => {
        const y = padT + plotH - frac * plotH;
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(width - padR, y);
        ctx.stroke();
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        ctx.fillText(String(Math.round(niceMax * frac)), padL - 6, y);
      });

      const gap = 8;
      const slot = plotW / data.length;
      const barWidth = Math.max(4, Math.min(40, slot - gap));

      data.forEach((d, i) => {
        const barHeight = (d.value / niceMax) * plotH;
        const x = padL + i * slot + (slot - barWidth) / 2;
        const y = padT + plotH - barHeight;
        ctx.fillStyle = barColor;
        const radius = Math.min(4, barWidth / 2);
        // Rounded-top bar via a manual path — canvas has no roundRect-with-
        // per-corner shorthand old enough browsers can rely on, so this
        // draws it explicitly rather than assuming `roundRect` exists.
        ctx.beginPath();
        ctx.moveTo(x, y + radius);
        ctx.arcTo(x, y, x + radius, y, radius);
        ctx.lineTo(x + barWidth - radius, y);
        ctx.arcTo(x + barWidth, y, x + barWidth, y + radius, radius);
        ctx.lineTo(x + barWidth, y + plotH);
        ctx.lineTo(x, y + plotH);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = textColor;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(d.label, x + barWidth / 2, padT + plotH + 6);
      });
    },
    [data, height, color, empty],
  );

  return (
    <div className="chart">
      {!empty && <canvas ref={canvasRef} role="presentation" />}
      <AccessibleTable title={title} columns={["Bucket", "Count"]} rows={data.map((d) => [d.label, String(d.value)])} />
    </div>
  );
}

/* ──────────────────────────────────── DonutChart ──────────────────────────────────── */

export interface DonutDatum {
  label: string;
  value: number;
  color: string;
}

export interface DonutChartProps {
  data: DonutDatum[];
  size?: number;
  title?: string;
}

export function DonutChart({ data, size = 160, title = "Status distribution" }: DonutChartProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const empty = total === 0;

  useCanvasPainter(
    canvasRef,
    (canvas) => {
      if (empty) return;
      const prepared = prepareCanvas(canvas, size);
      if (!prepared) return;
      const { ctx, width, height } = prepared;

      const cx = width / 2;
      const cy = height / 2;
      const outerR = Math.min(width, height) / 2 - 4;
      const innerR = outerR * 0.62;

      let angle = -Math.PI / 2;
      for (const d of data) {
        if (d.value === 0) continue;
        const slice = (d.value / total) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, outerR, angle, angle + slice);
        ctx.closePath();
        ctx.fillStyle = d.color;
        ctx.fill();
        angle += slice;
      }

      // Punch the donut hole using the panel background so it reads as a
      // hole regardless of what colors surround it.
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";

      const textColor = cssVar("--ink", "#111827");
      const faintColor = cssVar("--faint", "#9ca3af");
      ctx.fillStyle = textColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "700 18px var(--font-sans), sans-serif";
      ctx.fillText(String(total), cx, cy - 6);
      ctx.fillStyle = faintColor;
      ctx.font = "11px var(--font-sans), sans-serif";
      ctx.fillText("total", cx, cy + 12);
    },
    [data, size, total, empty],
  );

  return (
    <div className="chart chart--donut">
      {!empty && (
        <div className="chart__donut-row">
          <canvas ref={canvasRef} role="presentation" style={{ width: size, height: size }} />
          <ul className="chart__legend">
            {data
              .filter((d) => d.value > 0)
              .map((d) => (
                <li key={d.label} className="chart__legend-item">
                  <span className="chart__legend-swatch" style={{ background: d.color }} aria-hidden="true" />
                  <span className="chart__legend-label">{d.label}</span>
                  <span className="chart__legend-value">{d.value}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
      <AccessibleTable
        title={title}
        columns={["Status", "Count"]}
        rows={data.map((d) => [d.label, String(d.value)])}
      />
    </div>
  );
}

/* ──────────────────────────────────── HBarChart ──────────────────────────────────── */

export interface HBarDatum {
  label: string;
  value: number;
  max: number;
  color?: string;
}

export interface HBarChartProps {
  data: HBarDatum[];
  title?: string;
}

/**
 * Per-person workload bars. Unlike the other three charts this one is drawn
 * with DOM elements, not canvas — the accessible table below would otherwise
 * duplicate a layout that flexbox already renders correctly, and a fixed-row
 * list of named bars gains nothing from a raster surface. Kept in this file
 * because the caller treats it as one of the four chart primitives.
 */
export function HBarChart({ data, title = "Workload" }: HBarChartProps) {
  const empty = useMemo(() => data.length === 0 || data.every((d) => d.value === 0), [data]);
  const accent = cssVar("--chart-1", "#184669");

  if (empty) {
    return (
      <div className="chart">
        <AccessibleTable title={title} columns={["Person", "Value"]} rows={data.map((d) => [d.label, String(d.value)])} />
      </div>
    );
  }

  return (
    <div className="chart chart--hbar">
      <div className="hbar-list" aria-hidden="true">
        {data.map((d) => {
          const pct = d.max > 0 ? Math.min(100, (d.value / d.max) * 100) : 0;
          return (
            <div className="hbar-row" key={d.label}>
              {/* Titled because the label column is fixed-width: a longer name
                  ellipsises, and without this it cannot be read at all. */}
              <span className="hbar-row__label" title={d.label}>
                {d.label}
              </span>
              <span className="hbar-row__track">
                <span
                  className="hbar-row__fill"
                  style={{ width: `${pct}%`, background: d.color ?? accent }}
                />
              </span>
              <span className="hbar-row__value">{d.value}</span>
            </div>
          );
        })}
      </div>
      <AccessibleTable title={title} columns={["Person", "Value"]} rows={data.map((d) => [d.label, String(d.value)])} />
    </div>
  );
}

/* ──────────────────────────────────── accessible table ──────────────────────────────────── */

interface AccessibleTableProps {
  title: string;
  columns: [string, string];
  rows: [string, string][];
}

/**
 * Visually-hidden table mirroring the chart's data, so a screen reader user
 * gets the same numbers a sighted user reads off the canvas. `.sr-only` is
 * defined once in tokens.css and reused everywhere on the page.
 */
function AccessibleTable({ title, columns, rows }: AccessibleTableProps) {
  return (
    <table className="sr-only">
      <caption>{title}</caption>
      <thead>
        <tr>
          <th>{columns[0]}</th>
          <th>{columns[1]}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([a, b], i) => (
          <tr key={i}>
            <td>{a}</td>
            <td>{b}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
